import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { PrintDocument } from '@aeris/printing'
import { Badge, Button, notify } from '@aeris/ui'

import {
  ApiError,
  sendDeliveryRoute,
  type DeliveryRouteView,
} from '../lib/api.ts'
import { clockAfter } from '../lib/route-format.ts'
import { routePrintFor } from '../lib/route-print.ts'
import { useCompanyHeader } from '../lib/use-receipt-template.ts'

import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * O que o entregador leva (F267): a folha impressa e a rota no WhatsApp dele.
 * A rota vai sozinha ao despachar; aqui se manda de novo e se vê se foi.
 */

const SKIP_LABELS: Record<string, string> = {
  no_phone: 'sem WhatsApp do entregador',
  no_account: 'sem número da loja na WuzAPI',
  failed: 'não foi',
}

export const RouteActions = ({
  route,
  canSend,
}: {
  route: DeliveryRouteView
  canSend: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const company = useCompanyHeader()
  const [printable, setPrintable] = useState<PrintDocument | null>(null)
  const send = useMutation({
    mutationFn: async () => await sendDeliveryRoute(route.id),
    onSuccess: async () => {
      notify.success(`A rota foi para o WhatsApp de ${route.courierName}.`)
      await queryClient.invalidateQueries({ queryKey: ['delivery-routes'] })
    },
    onError: (cause: unknown) => {
      notify.error(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível mandar agora.'
      )
    },
  })

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
        onClick={() => {
          setPrintable(routePrintFor(route, company, new Date()))
        }}
      >
        Imprimir
      </Button>
      {route.status === 'planned' ? null : (
        <>
          {canSend ? (
            <Button
              size="sm"
              variant="secondary"
              disabled={send.isPending}
              leadingIcon={
                <MessageCircle aria-hidden="true" className="h-4 w-4" />
              }
              onClick={() => {
                send.mutate()
              }}
            >
              {route.sentAt === null ? 'Mandar no WhatsApp' : 'Mandar de novo'}
            </Button>
          ) : null}
          {route.sentAt !== null ? (
            <Badge tone="success">
              no WhatsApp às {clockAfter(new Date(route.sentAt), 0)}
            </Badge>
          ) : route.sendSkipReason === null ? null : (
            <Badge tone="warning">
              {SKIP_LABELS[route.sendSkipReason] ?? route.sendSkipReason}
            </Badge>
          )}
        </>
      )}
      {printable === null ? null : (
        <PrintDocumentDialog
          document={printable}
          description={`A folha da rota de ${route.courierName}`}
          initialPaper="a4"
          onClose={() => {
            setPrintable(null)
          }}
        />
      )}
    </div>
  )
}
